import {
  assignedPuntosVentaFromUser,
  resolveSalePuntosVenta,
  vendedorBlockedWithoutPv,
  type AssignedPuntoVenta,
  type PvUser,
} from "./pvAsignado";
import { sessionIsElevated } from "./modulePermissions";

type SessionIdentity = {
  id?: number | null;
  codigo?: string | null;
  username?: string | null;
};

export type SessionUserEvent = {
  userId?: number;
  codigo?: string;
  username?: string;
};

function asId(value: unknown): number | null {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function asText(value: unknown): string {
  return value == null ? "" : String(value);
}

/** True if `fresh` is the same logged-in person as `cached` (not another ABM row). */
export function isSameSessionUser(
  cached: SessionIdentity | null | undefined,
  fresh: SessionIdentity | null | undefined,
): boolean {
  if (!cached || !fresh) return false;
  const cachedId = asId(cached.id);
  const freshId = asId(fresh.id);
  if (cachedId && freshId && cachedId !== freshId) return false;
  if (cached.codigo && fresh.codigo && asText(cached.codigo) !== asText(fresh.codigo)) {
    return false;
  }
  if (
    cached.username &&
    fresh.username &&
    asText(cached.username).toLowerCase() !== asText(fresh.username).toLowerCase()
  ) {
    return false;
  }
  return Boolean(
    (cachedId && freshId) ||
      (cached.codigo && fresh.codigo) ||
      (cached.username && fresh.username),
  );
}

/**
 * Same-tab / other-tab `user:updated`: only refresh if the event is about
 * the cashier in this session.
 */
export function sessionEventTargetsUser(
  user: SessionIdentity | null | undefined,
  event: SessionUserEvent,
): boolean {
  if (!user) return false;
  if (event.codigo && user.codigo && asText(event.codigo) !== asText(user.codigo)) {
    return false;
  }
  const eventId = asId(event.userId);
  const userId = asId(user.id);
  if (eventId && userId && eventId !== userId) return false;
  if (
    event.username &&
    user.username &&
    asText(event.username).toLowerCase() !== asText(user.username).toLowerCase()
  ) {
    return false;
  }
  return Boolean(
    (event.codigo && asText(event.codigo) === asText(user.codigo)) ||
      (eventId && userId && eventId === userId) ||
      (event.username &&
        user.username &&
        asText(event.username).toLowerCase() === asText(user.username).toLowerCase()),
  );
}

function defaultCodigoOf(user: PvUser | Record<string, unknown> | null | undefined): string | null {
  if (!user) return null;
  const raw =
    (user as PvUser).puntoVentaDefault ??
    (user as PvUser).punto_venta_default ??
    (user as Record<string, unknown>).puntoVentaDefault ??
    (user as Record<string, unknown>).punto_venta_default;
  if (raw == null || raw === "") return null;
  return String(raw);
}

/**
 * Replace the login snapshot with `/opciones/usuarios/me/` (PV + asignados).
 * Does not touch JWT keys. If `fresh` is another user, `cached` wins.
 */
export function mergeSessionUser<T extends PvUser>(
  cached: T | null | undefined,
  fresh: PvUser | Record<string, unknown> | null | undefined,
): T | null | undefined {
  if (!fresh) return cached;
  if (!cached) return fresh as T;
  if (!isSameSessionUser(cached, fresh as SessionIdentity)) return cached;

  const rec = fresh as Record<string, unknown>;
  const puntoVentaDefault =
    defaultCodigoOf(fresh) ?? defaultCodigoOf(cached);
  const puntosVenta = Array.isArray(rec.puntosVenta)
    ? rec.puntosVenta
    : Array.isArray(rec.puntos_venta)
      ? rec.puntos_venta
      : cached.puntosVenta;
  const freshAdmin = rec.isAdmin ?? rec.is_admin;
  const freshSuper = rec.isSuperuser ?? rec.is_superuser;
  const freshElevated = rec.isElevated ?? rec.is_elevated;
  const cachedRec = cached as T & Record<string, unknown>;

  return {
    ...cached,
    ...fresh,
    puntoVentaDefault,
    punto_venta_default: puntoVentaDefault,
    puntosVenta,
    isAdmin: freshAdmin ?? cachedRec.isAdmin ?? cachedRec.is_admin,
    is_admin: rec.is_admin ?? rec.isAdmin ?? cachedRec.is_admin ?? cachedRec.isAdmin,
    isSuperuser: freshSuper ?? cachedRec.isSuperuser ?? cachedRec.is_superuser,
    is_superuser: rec.is_superuser ?? rec.isSuperuser ?? cachedRec.is_superuser ?? cachedRec.isSuperuser,
    isElevated: freshElevated ?? cachedRec.isElevated ?? cachedRec.is_elevated,
    is_elevated: rec.is_elevated ?? rec.isElevated ?? cachedRec.is_elevated ?? cachedRec.isElevated,
    modulosPermitidos: rec.modulosPermitidos ?? cachedRec.modulosPermitidos,
    modulePermissions: rec.modulePermissions ?? cachedRec.modulePermissions,
  } as T;
}

export function persistSessionUser(user: unknown): void {
  try {
    localStorage.setItem("currentUser", JSON.stringify(user));
  } catch {
    /* quota / private mode */
  }
}

export type SaleOperatorFresh = {
  user: PvUser | null | undefined;
  list: AssignedPuntoVenta[];
  selected: AssignedPuntoVenta | null;
  changed: boolean;
};

/**
 * App load / F5 / nueva factura: perfil fresco → Predeterminado de la venta.
 * Admin sin PV: `fetchCatalog` (primer PV del catálogo).
 */
export async function refreshOperatorForSale(
  cached: PvUser | null | undefined,
  fetchProfile: () => Promise<PvUser | Record<string, unknown> | null | undefined>,
  fetchCatalog?: () => Promise<AssignedPuntoVenta[]>,
): Promise<SaleOperatorFresh> {
  let user: PvUser | null | undefined = cached;
  try {
    const fresh = await fetchProfile();
    if (fresh && isSameSessionUser(cached as SessionIdentity, fresh as SessionIdentity)) {
      user = mergeSessionUser(cached, fresh) ?? cached;
    }
  } catch {
    user = cached;
  }

  const changed = JSON.stringify(user ?? null) !== JSON.stringify(cached ?? null);

  if (vendedorBlockedWithoutPv(user)) {
    return { user, list: [], selected: null, changed };
  }

  const assigned = assignedPuntosVentaFromUser(user).filter((pv) => pv.activo);
  let catalog: AssignedPuntoVenta[] = [];
  const needCatalog = assigned.length === 0 || sessionIsElevated(user);
  if (needCatalog && fetchCatalog) {
    try {
      catalog = await fetchCatalog();
    } catch {
      catalog = [];
    }
  }

  const { list, selected } = resolveSalePuntosVenta(user, catalog);
    return { user, list, selected, changed };
}

/** Rol con tilde: en ventas y presupuestos no se eligen personalizados ya cargados. */
export function bloqueaPersonalizadosExistentes(
  user:
    | {
        bloquearPersonalizadosExistentes?: boolean;
        bloquear_personalizados_existentes?: boolean;
      }
    | null
    | undefined,
): boolean {
  if (!user) return false;
  return Boolean(
    user.bloquearPersonalizadosExistentes || user.bloquear_personalizados_existentes,
  );
}
